import {
  Audit,
  AuditChecklistItem,
  AuditWithSummary,
  ChecklistItemUpdateRequest,
} from '@fsp/types';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchAudit, fetchAudits, updateChecklistItem } from '../services/audit';
import { useAuth } from './AuthContext';

interface AuditContextType {
  // List of all audits
  audits: AuditWithSummary[];
  setAudits: (audits: AuditWithSummary[]) => void;
  refreshAudits: () => Promise<void>;
  isLoadingAudits: boolean;

  // Current audit detail (with checklist items)
  currentAudit: Audit | null;
  checklistItems: AuditChecklistItem[];
  plan: { id: string; name: string } | null;
  release: { id: string; versionLabel: string } | null;
  refreshCurrentAudit: () => Promise<void>;
  isLoadingAudit: boolean;

  // Update checklist item
  updateItem: (itemId: string, data: ChecklistItemUpdateRequest) => Promise<void>;

  // Helper to get items by category
  getItemsByCategory: (category: string) => AuditChecklistItem[];

  // Helper to get all categories
  getCategories: () => string[];

  // Helper to get summary counts
  getSummary: () => {
    totalItems: number;
    checkedItems: number;
    satisfactoryItems: number;
    nonConformityItems: number;
    unresolvedCorrectiveActions: number;
    percentComplete: number;
  };
}

const AuditContext = createContext<AuditContextType | undefined>(undefined);

export const AuditProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { auditId: routeAuditId, planId } = useParams<{ auditId: string; planId: string }>();
  const [searchParams] = useSearchParams();
  const queryAuditId = searchParams.get('audit');

  // Use query param audit ID if present (for ?audit=id), otherwise fall back to route param (for /audits/:id)
  const auditId = queryAuditId || routeAuditId;

  const { organisationId } = useAuth();

  // State for audits list
  const [audits, setAudits] = useState<AuditWithSummary[]>([]);
  const [isLoadingAudits, setIsLoadingAudits] = useState(false);

  // State for current audit detail
  const [currentAudit, setCurrentAudit] = useState<Audit | null>(null);
  const [checklistItems, setChecklistItems] = useState<AuditChecklistItem[]>([]);
  const [plan, setPlan] = useState<{ id: string; name: string } | null>(null);
  const [release, setRelease] = useState<{ id: string; versionLabel: string } | null>(null);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);

  // Fetch audits list
  const refreshAudits = useCallback(async () => {
    if (!organisationId || !planId) {
      setAudits([]);
      return;
    }

    setIsLoadingAudits(true);
    try {
      const auditsList = await fetchAudits(organisationId, planId);
      setAudits(auditsList);
    } catch (error) {
      console.error('Failed to fetch audits:', error);
      setAudits([]);
    } finally {
      setIsLoadingAudits(false);
    }
  }, [organisationId, planId]);

  // Fetch current audit detail
  const refreshCurrentAudit = useCallback(async () => {
    if (!organisationId || !planId || !auditId) {
      setCurrentAudit(null);
      setChecklistItems([]);
      setPlan(null);
      setRelease(null);
      return;
    }

    setIsLoadingAudit(true);
    try {
      const data = await fetchAudit(organisationId, planId, auditId);
      setCurrentAudit(data.audit);
      setChecklistItems(data.checklistItems);
      setPlan(data.plan);
      setRelease(data.release);
    } catch (error) {
      console.error('Failed to fetch audit:', error);
      setCurrentAudit(null);
      setChecklistItems([]);
      setPlan(null);
      setRelease(null);
    } finally {
      setIsLoadingAudit(false);
    }
  }, [organisationId, planId, auditId]);

  // Update a checklist item
  const updateItem = useCallback(
    async (itemId: string, data: ChecklistItemUpdateRequest) => {
      if (!organisationId || !planId || !auditId) return;

      try {
        const updatedItem = await updateChecklistItem(
          organisationId,
          planId,
          auditId,
          itemId,
          data
        );

        // Update the local state
        setChecklistItems((items) =>
          items.map((item) => (item.id === itemId ? updatedItem : item))
        );
      } catch (error) {
        console.error('Failed to update checklist item:', error);
        throw error;
      }
    },
    [organisationId, planId, auditId]
  );

  // Helper to get items by category
  const getItemsByCategory = useCallback(
    (category: string): AuditChecklistItem[] => {
      return checklistItems
        .filter((item) => item.category === category)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    },
    [checklistItems]
  );

  // Helper to get all unique categories in order
  const getCategories = useCallback((): string[] => {
    const categories = new Map<string, number>();
    checklistItems.forEach((item) => {
      if (!categories.has(item.category)) {
        categories.set(item.category, item.sortOrder);
      }
    });
    return Array.from(categories.keys()).sort(
      (a, b) => categories.get(a)! - categories.get(b)!
    );
  }, [checklistItems]);

  // Helper to get summary counts
  const getSummary = useCallback(() => {
    const totalItems = checklistItems.length;
    const checkedItems = checklistItems.filter((item) => item.status !== 'NOT_CHECKED').length;
    const satisfactoryItems = checklistItems.filter((item) => item.status === 'SATISFACTORY')
      .length;
    const nonConformityItems = checklistItems.filter((item) => item.status === 'NON_CONFORMITY')
      .length;
    const unresolvedCorrectiveActions = checklistItems.filter(
      (item) => item.status === 'NON_CONFORMITY' && !item.isCorrectiveActionResolved
    ).length;
    const percentComplete = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

    return {
      totalItems,
      checkedItems,
      satisfactoryItems,
      nonConformityItems,
      unresolvedCorrectiveActions,
      percentComplete,
    };
  }, [checklistItems]);

  // Auto-fetch audits when organisationId or planId changes
  useEffect(() => {
    if (organisationId && planId) {
      refreshAudits();
    } else {
      setAudits([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, planId]);

  // Auto-fetch current audit when auditId changes
  useEffect(() => {
    if (organisationId && planId && auditId) {
      refreshCurrentAudit();
    } else {
      setCurrentAudit(null);
      setChecklistItems([]);
      setPlan(null);
      setRelease(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, planId, auditId]);

  const value: AuditContextType = {
    audits,
    setAudits,
    refreshAudits,
    isLoadingAudits,
    currentAudit,
    checklistItems,
    plan,
    release,
    refreshCurrentAudit,
    isLoadingAudit,
    updateItem,
    getItemsByCategory,
    getCategories,
    getSummary,
  };

  return <AuditContext.Provider value={value}>{children}</AuditContext.Provider>;
};

export const useAudit = (): AuditContextType => {
  const context = useContext(AuditContext);
  if (context === undefined) {
    throw new Error('useAudit must be used within an AuditProvider');
  }
  return context;
};
